import { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Box,
    Button,
    Chip,
    Grid
} from '@mui/material';
import Loader from '../../../../Components/Loader';
import InputConfig from '../../../../Components/InputConfig';
import { getSiteConfig } from '../../../../api/sites';
import { truncateText } from '../../../../utilities/helpers';
import useCommonStyles from '../../../../commonStyles';
import { SPLIT_CONFIG_TYPES, AB_SPLIT_TEST_CONSTANTS } from '../../../../core/constants';
import { getSectionNameToDisplay } from '../../../../pages/UnitConfigGenerator/Components/SiteConfig/ExperimentsContextSettings/helpers';

const { EXPERIMENT_SPLIT, NORMAL_SPLIT, MULTI_SPLIT } = SPLIT_CONFIG_TYPES;
const { AB_SPLIT_TYPE: { CONTROL, EXPERIMENT } } = AB_SPLIT_TEST_CONSTANTS;

function getSplitsBasedOnSplitType(videoSectionId, ads, splitType) {
    if (!ads) return {};

    const getMinimalAd = ad => (ad ? { name: ad.name, sectionRef: ad.sectionRef, multiSplitTestData: ad.multiSplitTestData } : null);

    switch (splitType) {
        case EXPERIMENT_SPLIT: {
            const controlAd = ads.find(
                ad =>
                    ad.videoSectionId === videoSectionId &&
                    ad.abSplitTestData?.splitType === CONTROL
            );
            if (!controlAd) return {};

            const experimentAd = ads.find(
                ad => ad.sectionRef === controlAd.abSplitTestData.counterpartSectionRef
            );
            return {
                controlSection: getMinimalAd(controlAd),
                experimentSection: getMinimalAd(experimentAd)
            };
        }

        case MULTI_SPLIT: {
            const controlAd = ads.find(
                ad =>
                    ad.videoSectionId === videoSectionId && ad.multiSplitTestData?.splitType === CONTROL
            );
            const multiSplitSections = ads.filter(
                ad =>
                    ad.videoSectionId === videoSectionId &&
                    ad.multiSplitTestData?.splitType === EXPERIMENT
            );
            return {
                controlSection: getMinimalAd(controlAd),
                multiSplitSections: multiSplitSections.map(getMinimalAd)
            };
        }

        case NORMAL_SPLIT: {
            const ad = ads.find(ad => ad.videoSectionId === videoSectionId && !ad.abSplitTestData);
            return { controlSection: getMinimalAd(ad) };
        }

        default: {
            return {};
        }
    }
}

function ExperimentCard({ ads, sectionLevelSplit, videoSectionId }) {
    const commonStyleClasses = useCommonStyles();

    const sectionSplitData = useMemo(
        () => sectionLevelSplit?.splits?.[videoSectionId],
        [sectionLevelSplit, videoSectionId]
    );

    const {
        controlSection,
        experimentSection,
        multiSplitSections = []
    } = getSplitsBasedOnSplitType(videoSectionId, ads, sectionSplitData?.type);

    const splitPercentageMap = useMemo(() => {
        if (!sectionSplitData?.controlExperimentSplits) return {};
        return sectionSplitData.controlExperimentSplits;
    }, [sectionSplitData]);

    const getSplitPercentage = section => {
        if (!section || !section.sectionRef) return 0;
        return splitPercentageMap[section.sectionRef]?.splitPercentage || 0;
    };

    return (
        <Box className={commonStyleClasses.basicOutlinedBox} style={{ width: '95%' }}>
            <Box style={{ width: '90%' }}>
                <Chip label={sectionSplitData?.type} color="primary" style={{ float: 'right' }} />
            </Box>

            {videoSectionId && sectionSplitData && (
                <Box marginTop={'6rem'}>
                    {/* Video Section Split Card for EXPERIMENT_SPLIT */}
                    {sectionSplitData.type === EXPERIMENT_SPLIT && (
                        <Grid container spacing={1} alignItems="center" style={{ width: '100%' }}>
                            <Grid item xs={12}>
                                <InputConfig
                                    label={`${truncateText(controlSection?.name)} (control)`}
                                    inputType="number"
                                    inputStyles={{ width: '100%', margin: '1rem 0' }}
                                    labelStyles={{ fontSize: '1.7rem' }}
                                    isObjectEditor={false}
                                    directValue={getSplitPercentage(controlSection)}
                                    editable={false}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <InputConfig
                                    label={truncateText(experimentSection?.name)}
                                    inputType="number"
                                    inputStyles={{ width: '100%', margin: '1rem 0' }}
                                    labelStyles={{ fontSize: '1.7rem' }}
                                    isObjectEditor={false}
                                    directValue={getSplitPercentage(experimentSection)}
                                    editable={false}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* Video Section Split Card for NORMAL_SPLIT */}
                    {sectionSplitData.type === NORMAL_SPLIT && (
                        <Grid container spacing={1} alignItems="center" style={{ width: '100%' }}>
                            {/* Section Name */}
                            <Grid item xs={12}>
                                <InputConfig
                                    label={`${truncateText(controlSection?.name)}`}
                                    inputType="number"
                                    inputStyles={{ width: '100%', margin: '1rem 0' }}
                                    labelStyles={{ fontSize: '1.7rem' }}
                                    isObjectEditor={false}
                                    directValue={sectionSplitData.splitPercentage}
                                    editable={false}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {/* Video Section Split Card for MULTI_SPLIT */}
                    {sectionSplitData.type === MULTI_SPLIT && (
                        <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            style={{ width: '100%', marginTop: '1rem' }}
                        >
                            {/* Control Section */}
                            <Grid item xs={12}>
                                <InputConfig
                                    label={`${truncateText(controlSection?.name)}  (control)`}
                                    inputType="number"
                                    inputStyles={{ width: '100%', margin: '1rem 0' }}
                                    labelStyles={{ fontSize: '1.7rem' }}
                                    isObjectEditor={false}
                                    directValue={getSplitPercentage(controlSection)}
                                    editable={false}
                                />
                            </Grid>

                            {/* Multi-Split Sections */}
                            {multiSplitSections.length > 0 &&
                                multiSplitSections.map((section, index) => (
                                    <Grid item xs={12} key={index}>
                                        <InputConfig
                                            label={truncateText(getSectionNameToDisplay(section))}
                                            inputType="number"
                                            inputStyles={{ width: '100%', margin: '1rem 0' }}
                                            labelStyles={{ fontSize: '1.7rem' }}
                                            isObjectEditor={false}
                                            directValue={getSplitPercentage(section)}
                                            editable={false}
                                        />
                                    </Grid>
                                ))}
                        </Grid>
                    )}
                </Box>
            )}
        </Box>
    );
}

function ExperimentDialog({ selectedRowData, modalOpen, handleModalClose }) {
    const [siteConfig, setSiteConfig] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function fetchConfig() {
            setLoading(true);
            try {
                const { data: siteData } = await getSiteConfig(selectedRowData.siteId);
                const site = siteData?.data?.site;
                setSiteConfig(site);
            } catch (e) {
                setSiteConfig(null);
            } finally {
                setLoading(false);
            }
        }
        fetchConfig();
    }, [selectedRowData.siteId]);

    return (
        <Dialog open={modalOpen} onClose={handleModalClose} maxWidth="md" fullWidth>
            <DialogTitle>Split Configuration Details</DialogTitle>
            <DialogContent>
                <Box mb={2}>
                    <Typography variant="body2" color="textSecondary">
                        <b>Site ID:</b> {selectedRowData.siteId} &nbsp;|&nbsp;
                        <b>Domain:</b> {selectedRowData.domain}
                    </Typography>
                </Box>
                {loading ? (
                    <Loader />
                ) : !siteConfig ? (
                    <Typography color="error">Failed to load site config.</Typography>
                ) : selectedRowData.sectionLevelSplit?.splits ? (
                    Object.entries(selectedRowData.sectionLevelSplit.splits).map(
                        ([videoSectionId]) => (
                            <Box key={videoSectionId} mb={3}>
                                <ExperimentCard
                                    videoSectionId={videoSectionId}
                                    ads={siteConfig.ads}
                                    sectionLevelSplit={siteConfig.sectionLevelSplit}
                                />
                            </Box>
                        )
                    )
                ) : (
                    <Typography color="error">
                        Site must be running on Selective Rollout.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={handleModalClose}>Close</Button>
                <Button
                    variant="contained"
                    color="primary"
                    href={`/update-config/${selectedRowData.siteId}`}
                    target="_blank"
                    rel="noopener"
                >
                    Visit ↗
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default ExperimentDialog;